import React, { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from './Header';
import ByrdleArchiveStrip from './ByrdleArchiveStrip';
import ByrdleClueCard from './ByrdleClueCard';
import SEO from './SEO';
import { useByrdle } from '../context/ByrdleContext';
import { Sparkles, ChevronLeft, ChevronRight } from 'lucide-react';
import NotFoundPage from './NotFoundPage';

import Sidebar from './Sidebar';

const ByrdlePage = ({ isToday, date: propDate }) => {
  const params = useParams();
  const { puzzles, loading } = useByrdle();
  
  const groupedPuzzles = useMemo(() => {
    const groups = {};
    puzzles.forEach(puzzle => {
      const date = new Date(puzzle.date);
      const key = date.toLocaleString('default', { month: 'long', year: 'numeric' });
      if (!groups[key]) {
        groups[key] = [];
      }
      if (groups[key].length < 4) {
        groups[key].push(puzzle);
      }
    });
    return groups;
  }, [puzzles]);
  
  if (loading) return null;

  let selectedPuzzle = null;

  if (isToday) {
    selectedPuzzle = puzzles.find(p => p.date === propDate);
  } else {
    // Try to find by number if URL matches byrdle-X
    const numberMatch = params.puzzleId ? params.puzzleId.match(/byrdle-(-?\d+)/) : null;
    if (numberMatch) {
      const number = numberMatch[1];
      selectedPuzzle = puzzles.find(p => p.puzzleNumber === number);
    } else {
         // Try to find by date if URL is just a date string or doesn't match the pattern
         // Also handle "byrdle-NUMBER" where NUMBER is just digits without "byrdle-" prefix in regex if needed,
         // but the user asked for "byrdle-1474" which matches the regex above.
         // However, if the regex fails, we fall back to date.
         
         // User requested: "make it simply the puzzle title (like byrdle-1474)"
         // If title is "Byrdle #1474", p.title might match differently.
         // Let's also try to match by title slugified if date fails.
         selectedPuzzle = puzzles.find(p => p.date === params.puzzleId);

         if (!selectedPuzzle) {
            // Fallback: Check if params.puzzleId looks like "byrdle-1474" and try to extract 1474
            const simpleMatch = params.puzzleId.match(/byrdle-(\d+)/);
            if (simpleMatch) {
                const num = simpleMatch[1];
                selectedPuzzle = puzzles.find(p => p.puzzleNumber === num);
            }
         }
    }
  }
  
  if (!selectedPuzzle && !isToday) return <NotFoundPage />;
  if (!selectedPuzzle) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#f8f9fa] p-4 text-center">
        <Header date={new Date().toISOString()} title="Byrdle" />
        <div className="flex-grow flex items-center justify-center">
            <p className="text-gray-500 text-lg">No Byrdle puzzle found for today. Please check back later.</p>
        </div>
      </div>
    );
  }

  const { date, title, clues } = selectedPuzzle;

  const schema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": isToday ? `Today's Byrdle Hints & Answers: ${title}` : `${title} Hints and Answers`,
    "description": `Get hints and answers for ${title} from ${new Date(date).toLocaleDateString()}. Solve the daily choral music word puzzle.`,
    "image": `${window.location.origin}/byrdle-logo.png`, 
    "datePublished": new Date(date).toISOString(),
    "dateModified": new Date(date).toISOString(),
    "author": {
      "@type": "Organization",
      "name": "PocketHints"
    },
    "publisher": {
      "@type": "Organization",
      "name": "PocketHints",
      "logo": {
        "@type": "ImageObject",
        "url": `${window.location.origin}/site-logo.png`
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": window.location.href
    }
  };

  const breadcrumbs = [
    { name: "Home", item: window.location.origin },
    { name: "Byrdle Archive", item: `${window.location.origin}/byrdle-archive` },
    { name: isToday ? "Today's Puzzle" : `${title}`, item: window.location.href }
  ];

  return (
    <div className="flex flex-col w-full pb-12">
      <SEO 
        title={isToday ? "Today's Byrdle Hints & Answers" : `${title} - ${new Date(date).toLocaleDateString()}`}
        description={`Get hints and answers for ${title} from ${new Date(date).toLocaleDateString()}. Solve the clues to find the hidden musical words.`}
        url={window.location.href}
        schema={schema}
        breadcrumbs={breadcrumbs}
      />
      <Header date={date} title={title} gameName="Daily Byrdle" />
      
      <ByrdleArchiveStrip 
        puzzles={puzzles} 
        selectedDate={date} 
      />


      {isToday && (
        <div className="max-w-4xl mx-auto px-4 mb-12 text-center">
          <div className="bg-white border border-gray-200 rounded-2xl p-6 md:p-8 shadow-sm">
            <h2 className="text-2xl font-black text-gray-900 mb-4">
              Daily Byrdle Hints & Answers
            </h2>
            <div className="prose prose-blue mx-auto text-gray-600 leading-relaxed mb-8 entry-content">
              <p className="mb-4">
                <strong>Today's Byrdle Answers</strong> are ready for you! Stuck on today's choral music puzzle? We've got the hints you need to guess the secret word!
              </p>
              <p className="mb-4">
                <strong>Byrdle</strong> is a daily word guessing game for choral music lovers. Each day you have to guess a five-letter word related to choral music.
              </p>
              <p className="mb-4">
                Use our progressive hints to solve the puzzle without spoiling the fun immediately.
              </p>
              <p className="text-xs text-gray-400 italic mt-4">
                Disclaimer: We are not affiliated with the creators of Byrdle. We just love the game!
              </p>
            </div>

            <div className="text-center">
              <a 
                href="https://www.byrdle.net/"
                target="_blank"
                rel="noopener noreferrer" 
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-brand-blue text-white text-lg font-bold rounded-xl shadow-lg hover:bg-blue-500 hover:scale-105 transition-all duration-200"
              >
                Play Byrdle
              </a>
              <p className="mt-3 text-xs font-medium text-gray-400 uppercase tracking-wide">
                OFFICIAL GAME LINK
              </p>
            </div>
          </div>
        </div>
      )}
      
      <main className="flex-grow max-w-7xl mx-auto px-4 sm:px-6 w-full flex flex-col lg:flex-row gap-8">
        <div className="flex-1 min-w-0">
          <section className="w-full relative">
            <div className="sticky top-24 z-30 bg-white border-b border-gray-200 py-3 mb-6 lg:static lg:bg-transparent lg:border-none rounded-xl lg:rounded-none px-4 lg:px-0 shadow-sm lg:shadow-none">
              <h2 className="text-2xl font-black text-gray-900 flex items-center gap-3">
                Clue & Hints
              </h2>
            </div>
            
            <p className="text-gray-600 mb-6 leading-relaxed">
              Here is the clue for today's puzzle. Click "Hints" to expand them or "Reveal Answer" if you're stumped.
            </p>


            <div className="space-y-4">
            {clues.map((clue) => (
              <ByrdleClueCard 
                key={`${date}-${clue.id}`} 
                clue={clue} 
                date={date} 
                isToday={isToday}
                puzzleNumber={selectedPuzzle.puzzleNumber}
                puzzleTitle={title}
              />
            ))}
            </div>
          </section>


          {/* Play More Section */}
          <section className="mt-8 mb-8 text-center bg-white rounded-3xl p-8 border border-gray-200 shadow-sm">
            <div className="flex flex-col items-center gap-6">
              <img 
                src={process.env.PUBLIC_URL + '/byrdle-logo.png'}
                alt="Byrdle Logo" 
                className="w-24 h-24 object-contain drop-shadow-sm"
              />
              <h2 className="text-3xl font-black text-gray-900">
                Want to Solve More?
              </h2>
              <div className="flex flex-col items-center justify-center gap-4 w-full max-w-md">
                <Link 
                  to="/todays-pocket-grids-clues-answers"
                  className="flex items-center justify-between w-full px-6 py-4 bg-white text-brand-blue border-2 border-brand-blue text-lg font-bold rounded-xl shadow-sm hover:bg-brand-blue/5 hover:scale-105 transition-all duration-200"
                >
                  <div className="flex items-center gap-3">
                    <img src={process.env.PUBLIC_URL + '/pocket-grids-logo.png'} alt="Pocket Grids" className="w-8 h-8 object-contain" />
                    <span>Pocket Grids Hints</span>
                  </div>
                  <ChevronLeft className="w-5 h-5 rotate-180" />
                </Link>
                <Link 
                  to="/todays-syllo-clues-answers"
                  className="flex items-center justify-between w-full px-6 py-4 bg-white text-brand-blue border-2 border-brand-blue text-lg font-bold rounded-xl shadow-sm hover:bg-brand-blue/5 hover:scale-105 transition-all duration-200"
                >
                  <div className="flex items-center gap-3">
                    <img src={process.env.PUBLIC_URL + '/Syllo-logo.png'} alt="Syllo" className="w-8 h-8 object-contain" />
                    <span>Syllo Hints</span>
                  </div>
                  <ChevronLeft className="w-5 h-5 rotate-180" />
                </Link>
                <Link 
                  to="/todays-hot-and-cold-clues-answers"
                  className="flex items-center justify-between w-full px-6 py-4 bg-white text-brand-blue border-2 border-brand-blue text-lg font-bold rounded-xl shadow-sm hover:bg-brand-blue/5 hover:scale-105 transition-all duration-200"
                >
                  <div className="flex items-center gap-3">
                    <img src={process.env.PUBLIC_URL + '/hotandcold-logo.png'} alt="Hot and Cold" className="w-8 h-8 object-contain" />
                    <span>Hot & Cold Hints</span>
                  </div>
                  <ChevronLeft className="w-5 h-5 rotate-180" />
                </Link>
                <Link 
                  to="/daily-guess-solver"
                  className="flex items-center justify-between w-full px-6 py-4 bg-white text-brand-blue border-2 border-brand-blue text-lg font-bold rounded-xl shadow-sm hover:bg-brand-blue/5 hover:scale-105 transition-all duration-200"
                >
                  <div className="flex items-center gap-3">
                    <img src={process.env.PUBLIC_URL + '/dailyguess-logo.png'} alt="DailyGuess" className="w-8 h-8 object-contain" />
                    <span>DailyGuess Solver</span>
                  </div>
                  <ChevronLeft className="w-5 h-5 rotate-180" />
                </Link>
              </div>
            </div>
          </section>

          {/* SEO Content Section */}
          <section className="mt-12 bg-white rounded-3xl p-8 border border-gray-200 shadow-sm">
            <h2 className="text-2xl font-black text-gray-900 mb-6">About Byrdle</h2>
            
            <div className="space-y-8 entry-content">
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">How does Byrdle work?</h3>
                <p className="text-gray-600 leading-relaxed">
                  Byrdle is a daily word guessing game specifically designed for choral music lovers. Each day, you have six attempts to guess a five-letter word related to choral music, composers, or musical terms. After each guess, the color of the tiles changes to show how close your guess was to the word.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Why we do this</h3>
                <p className="text-gray-600 leading-relaxed">
                  Choral music terminology can be tricky, and sometimes you just need a little help! We created this helper site to provide progressive hints for the daily Byrdle puzzle. This way, you can get a nudge in the right direction without having the answer completely spoiled for you.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Frequently Asked Questions</h3>
                <div className="space-y-4">
                  <div>
                    <h4 className="font-bold text-gray-800">When does the new Byrdle puzzle come out?</h4>
                    <p className="text-gray-600">A new puzzle is released every day at midnight local time.</p>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-800">Are the words always related to choral music?</h4>
                    <p className="text-gray-600">Yes! The words are always related to choral music, composers, musical terms, or instruments.</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Archive Section */}
          <section className="mt-12 bg-white rounded-3xl p-8 border border-gray-200 shadow-sm">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-black text-gray-900 mb-4">Byrdle Archive</h2>
              <p className="text-gray-600 text-lg">Browse our collection of past daily crossword guides.</p>
            </div>
            <div className="space-y-12">
              {Object.entries(groupedPuzzles).slice(0, 3).map(([monthYear, monthPuzzles]) => (
                <div key={monthYear} className="animate-fade-in-up">
                  <h3 className="text-xl font-bold text-gray-400 uppercase tracking-wider mb-6 border-b border-gray-200 pb-2">
                    {monthYear}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {monthPuzzles.map((puzzle) => {
                       const pDate = new Date(puzzle.date);
                       const dayName = pDate.toLocaleDateString('en-US', { weekday: 'long' });
                       const dayNum = pDate.getDate();
                       
                       return (
                        <Link 
                          key={puzzle.date} 
                          to={`/byrdle/byrdle-${puzzle.puzzleNumber || puzzle.date}`}
                          className="group bg-white border border-gray-200 rounded-xl p-5 hover:border-brand-blue hover:shadow-md transition-all flex items-center justify-between"
                        >
                          <div className="flex items-center gap-4">
                            <div className="bg-brand-blue/10 text-brand-blue font-bold text-xl w-12 h-12 rounded-lg flex items-center justify-center group-hover:bg-brand-blue group-hover:text-white transition-colors">
                              {dayNum}
                            </div>
                            <div>
                              <div className="font-bold text-gray-900">{puzzle.title}</div>
                              <div className="text-sm text-gray-500 font-medium">{dayName}</div>
                            </div>
                          </div>
                          <ChevronRight className="w-5 h-5 text-gray-300 group-hover:text-brand-blue transition-colors" />
                        </Link>
                       );
                    })}
                  </div>
                </div>
              ))}
            </div>
            
            <div className="mt-10 text-center">
              <Link 
                to="/byrdle-archive"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-gray-100 text-gray-900 text-lg font-bold rounded-xl shadow-sm hover:bg-gray-200 hover:scale-105 transition-all duration-200"
              >
                View Full Archive
              </Link>
            </div>
          </section>
        </div>

        {/* Sidebar (Desktop Only) */}
        <div className="hidden lg:block w-80 flex-shrink-0">
          <div>
            <Sidebar />
          </div>
        </div>
      </main>
    </div>
  );
};

export default ByrdlePage;

